import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const emptyForm = {
  name: '',
  description: '',
  visibility: 'public',
  rules: '',
  imageUrl: '',
  creatorUserId: '',
};

function GroupTracker() {
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [visibility, setVisibility] = useState('all');
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminAPI.getGroupStats();
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to load group stats:', err);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (visibility !== 'all') params.visibility = visibility;
      if (source !== 'all') params.source = source;
      if (search.trim()) params.search = search.trim();
      const res = await adminAPI.getGroups(params);
      setGroups(res.data.groups || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [page, visibility, source, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const searchUsers = async () => {
    if (!userQuery.trim()) return;
    try {
      setSearchingUsers(true);
      const res = await adminAPI.getAllUsers({ search: userQuery.trim(), limit: 10 });
      setUserResults(res.data.users || res.data || []);
    } catch (err) {
      console.error('User search failed:', err);
    } finally {
      setSearchingUsers(false);
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedCreator(null);
    setUserQuery('');
    setUserResults([]);
    setCreateError('');
    setShowCreate(true);
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setCreateError('');
    try {
      const res = await adminAPI.uploadGroupImage(file);
      setForm((f) => ({ ...f, imageUrl: res.data.url }));
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setCreateError('Group name is required.'); return; }
    if (!selectedCreator) { setCreateError('Pick an existing user to be the group creator.'); return; }
    setCreating(true);
    setCreateError('');
    try {
      await adminAPI.createGroup({ ...form, creatorUserId: selectedCreator._id || selectedCreator.id });
      setShowCreate(false);
      fetchStats();
      fetchGroups();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Group Tracker</h1>
          <p style={{ color: '#667085', marginTop: 4 }}>Track public and private group creation, and create groups as admin.</p>
        </div>
        <button onClick={openCreate} style={btnPrimary}>+ Create Group</button>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            ['Total groups', stats.total],
            ['Public', stats.public],
            ['Private', stats.private],
            ['Admin-created', stats.adminCreated],
            ['User-created', stats.userCreated],
            ['Last 7 days', stats.createdLast7d],
          ].map(([label, value]) => (
            <div key={label} style={statCard}>
              <div style={{ fontSize: 12, color: '#98a2b3' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{value ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search by group name…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={input} />
        <select value={visibility} onChange={(e) => { setVisibility(e.target.value); setPage(1); }} style={input}>
          <option value="all">All visibility</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
        </select>
        <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} style={input}>
          <option value="all">All sources</option>
          <option value="admin">Admin-created</option>
          <option value="user">User-created</option>
        </select>
      </div>

      {error && <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>}

      <div style={{ border: '1px solid #e4e7ec', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Cover', 'Name', 'Visibility', 'Source', 'Creator', 'Members', 'Created'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={td}>Loading…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={7} style={td}>No groups found.</td></tr>
            ) : groups.map((g) => (
              <tr key={g.id}>
                <td style={td}>
                  {g.imageUrl
                    ? <img src={g.imageUrl} alt={g.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
                    : <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f2f4f7' }} />}
                </td>
                <td style={td}>{g.name}</td>
                <td style={td}><span style={{ textTransform: 'capitalize' }}>{g.visibility}</span></td>
                <td style={td}>{g.isAdminCreated ? `Admin (${g.createdByAdminEmail || 'unknown'})` : 'User'}</td>
                <td style={td}>{g.creator?.profileName || g.creator?.username || g.creator?.email || '—'}</td>
                <td style={td}>{g.memberCount}</td>
                <td style={td}>{g.createdAt ? new Date(g.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btnSecondary}>Prev</button>
          <span>Page {pagination.page} of {pagination.pages}</span>
          <button disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)} style={btnSecondary}>Next</button>
        </div>
      )}

      {showCreate && (
        <div style={overlay} onClick={() => setShowCreate(false)}>
          <form style={modal} onClick={(e) => e.stopPropagation()} onSubmit={handleCreate}>
            <h3 style={{ marginTop: 0 }}>Create group as admin</h3>
            {createError && <div style={{ color: '#b42318', marginBottom: 10 }}>{createError}</div>}
            <label style={label}>Name</label>
            <input style={input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            <label style={label}>Description</label>
            <textarea style={{ ...input, height: 60 }} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <label style={label}>Visibility</label>
            <select style={input} value={form.visibility} onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
            <label style={label}>Cover image</label>
            {form.imageUrl && (
              <img src={form.imageUrl} alt="Cover preview" style={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 8, marginBottom: 6 }} />
            )}
            <input type="file" accept="image/*" onChange={handleImagePick} disabled={uploadingImage} />
            {uploadingImage && <div style={{ fontSize: 12.5, color: '#667085', marginTop: 4 }}>Uploading…</div>}

            <label style={label}>Creator account (existing app user)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...input, flex: 1 }} value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search by name, username or email" />
              <button type="button" style={btnSecondary} onClick={searchUsers} disabled={searchingUsers}>
                {searchingUsers ? '…' : 'Search'}
              </button>
            </div>
            {userResults.length > 0 && (
              <div style={{ border: '1px solid #e4e7ec', borderRadius: 8, marginTop: 6, maxHeight: 140, overflowY: 'auto' }}>
                {userResults.map((u) => (
                  <div key={u._id || u.id} onClick={() => setSelectedCreator(u)}
                    style={{
                      padding: '8px 10px', cursor: 'pointer',
                      background: (selectedCreator?._id || selectedCreator?.id) === (u._id || u.id) ? '#eef0ff' : 'transparent',
                    }}>
                    {u.profileName || u.username || u.email} <span style={{ color: '#98a2b3' }}>({u.email})</span>
                  </div>
                ))}
              </div>
            )}
            {selectedCreator && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: '#12b76a' }}>
                Selected: {selectedCreator.profileName || selectedCreator.username || selectedCreator.email}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button type="button" style={btnSecondary} onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" style={btnPrimary} disabled={creating || uploadingImage}>{creating ? 'Creating…' : 'Create group'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const statCard = { border: '1px solid #e4e7ec', borderRadius: 10, padding: 14 };
const input = { border: '1px solid #e4e7ec', borderRadius: 8, padding: '8px 10px', fontSize: 13.5 };
const label = { fontSize: 12.5, fontWeight: 600, color: '#475467', display: 'block', margin: '10px 0 4px' };
const th = { textAlign: 'left', padding: '10px 14px', fontSize: 12, color: '#98a2b3', textTransform: 'uppercase' };
const td = { padding: '10px 14px', borderTop: '1px solid #e4e7ec', fontSize: 13.5 };
const btnPrimary = { background: '#5b5fef', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(16,24,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal = { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 480, maxHeight: '86vh', overflowY: 'auto' };

export default GroupTracker;
